import * as React from 'react';

import * as api from '@gitbook/api-client';
import { diffRevisionPages } from '@gitbook/hive-core';
import { useHiveClient, useHiveRevision } from '@gitbook/hive-react';
import { Icon } from '@gitbook/spine-icons';
import { Button, CircularProgress, Heading } from '@gitbook/spine-react';
import { getSuspense } from '@gitbook/util-suspense-cache';

import { useLoggedInUser } from '../../../state';
import { ChangeRequestReviewButton } from '../ChangeRequestReviewButton';
import { ChangeRequestNoChanges } from './ChangeRequestNoChanges';
import { PageDiffList } from './PageDiffList';
import { useViewedChanges } from './viewedChanges';
import { WithChangeRequestRevision } from './WithChangeRequestRevision';

export type ChangeRequestChangesProps = {
    space: api.Space;
    changeRequest: api.ChangeRequest;
};

/**
 * Fetch and display all the pages that have been changed in the change request.
 */
export function ChangeRequestChanges(props: ChangeRequestChangesProps) {
    return (
        <WithChangeRequestRevision {...props}>
            <InnerChangeRequestChanges {...props} />
        </WithChangeRequestRevision>
    );
}

function InnerChangeRequestChanges(props: ChangeRequestChangesProps) {
    const { space, changeRequest } = props;

    const currentUser = useLoggedInUser();
    const { revision: currentRevision } = useHiveRevision();

    const hiveClient = useHiveClient();
    const baseRevision = getSuspense(
        hiveClient.store.assertRevision(
            space.id,
            changeRequest.revisionMergedAncestor ?? changeRequest.revisionInitial
        )
    );

    const changes = React.useMemo(
        () => diffRevisionPages(currentRevision, baseRevision),
        [currentRevision, baseRevision]
    );

    const pageDiffEntries = React.useMemo(
        () =>
            Array.from(changes.diffs.entries(), ([key, pageDiff]) => ({ key, pageDiff })).sort(
                (a, b) => a.key.localeCompare(b.key)
            ),
        [changes]
    );

    const viewedChanges = useViewedChanges({ changeRequestId: changeRequest.id });
    const viewedChangesCount = viewedChanges.size;

    return (
        <div className="flex flex-1 flex-col [--changes-header-height:78px]">
            <div className="sticky top-0 z-20 flex h-[var(--changes-header-height)] flex-row items-center justify-between gap-3 bg-base">
                <Heading level={2} className="text-secondary text-ui-heading-small">
                    Changes
                </Heading>
                <div className="flex flex-row items-center gap-3">
                    {pageDiffEntries.length > 0 ? (
                        <div className="flex flex-row items-center gap-2">
                            <CircularProgress
                                segments={[
                                    {
                                        value: (100 * viewedChangesCount) / pageDiffEntries.length,
                                        kind: 'done',
                                    },
                                ]}
                                className="size-5"
                            />
                            <span className="flex flex-row items-center gap-1 text-muted text-ui-small tabular-nums">
                                <span className="text-base">{viewedChangesCount}</span>
                                {'/'}
                                <span className="text-base">{pageDiffEntries.length}</span>
                                {' viewed'}
                            </span>
                        </div>
                    ) : null}
                    <Button
                        kind="secondary"
                        label="Comments"
                        tooltip={{
                            label: 'View all comments in this change request',
                        }}
                        size="medium"
                        leadingIcon={Icon.Comment}
                        badge={{
                            label: changeRequest.comments,
                            kind: 'secondary',
                        }}
                        href={{
                            current: {
                                sideSheet: { type: 'comments' },
                            },
                        }}
                    />
                    {(changeRequest.status === api.ChangeRequestStatus.Open ||
                        changeRequest.status === api.ChangeRequestStatus.Draft) &&
                    space.permissions.review &&
                    currentUser.uid !== changeRequest.createdBy.id ? (
                        <ChangeRequestReviewButton
                            space={space.id}
                            changeRequest={changeRequest.id}
                            className="shrink-0"
                            kind="primary"
                            size="medium"
                        />
                    ) : null}
                </div>
            </div>
            {pageDiffEntries.length === 0 ? (
                <ChangeRequestNoChanges space={space} changeRequest={changeRequest} />
            ) : (
                <PageDiffList
                    entries={pageDiffEntries}
                    organizationId={space.organization}
                    baseRevision={baseRevision}
                    currentRevision={currentRevision}
                    changeRequest={changeRequest}
                    viewedChanges={viewedChanges}
                />
            )}
        </div>
    );
}
